import { Col, Row } from "antd";
import classNames from "classnames";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import HelpYellow from "shared/components/SVG/LMF/HelpYellow";
import QuickActionRed from "shared/components/SVG/LMF/QuickActionRed";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM, GENDER } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import CustomModal from "../Components/CustomModal";
import Footer from "../Components/Footer";
import Head from "../Components/Head";
import InfoBox from "../Components/InfoBox";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Radio from "../Components/Radio";
import InputChecked from "../Components/svg/check_small.svg";
import InputError from "../Components/svg/error.svg";
import validation from "./validation";

const View = ({ onNext, initialValues, locked, onBack }) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);

  const TRANSLATION = t("B2C_LMF.ADHERENT_DETAILS", { returnObjects: true });
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const [openModalPhone, setOpenModalPhone] = useState(false);
  const [openModalEmail, setOpenModalEmail] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues,
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
  });

  return (
    <>
      <CustomModal
        open={openModalPhone}
        setOpen={setOpenModalPhone}
        title={TRANSLATION.MODAL_PHONE.TITLE}
        description={TRANSLATION.MODAL_PHONE.DESC}
        TRANSLATION={TRANSLATION}
        width={isMobile ? "90%" : 500}
        closeText={TRANSLATION.MODAL_PHONE.CLOSE}
      ></CustomModal>

      <CustomModal
        open={openModalEmail}
        setOpen={setOpenModalEmail}
        title={TRANSLATION.MODAL_EMAIL.TITLE}
        description={TRANSLATION.MODAL_EMAIL.DESC}
        TRANSLATION={TRANSLATION}
        width={isMobile ? "90%" : 500}
        closeText={TRANSLATION.MODAL_EMAIL.CLOSE}
      ></CustomModal>

      <Layout>
        <Head
          description={TRANSLATION.DESCRIPTION}
          title={TRANSLATION.TITLE}
          onBack={onBack}
        />
        <div className="__LMF--content LMF-content lmf__form">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={24} lg={24}>
              <Radio
                title={TRANSLATION.FORM.GENDER.LABEL}
                type="simple"
                disabled={false}
                values={[
                  {
                    value: GENDER.MALE,
                    label: TRANSLATION.FORM.GENDER.OPTIONS.MR,
                  },
                  {
                    value: GENDER.FEMALE,
                    label: TRANSLATION.FORM.GENDER.OPTIONS.MRS,
                  },
                ]}
                {...bindInputProps({
                  name: `contact.user.gender`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={(value) => {
                  setFieldValue(`contact.user.gender`, value);
                }}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.LAST_NAME.LABEL}
                placeholder={TRANSLATION.FORM.LAST_NAME.PLACEHOLDER}
                className={classNames({
                  valid: get(values, "contact.user.lastname"),
                })}
                {...bindInputProps({
                  name: `contact.user.lastname`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  setFieldValue(`contact.user.lastname`, value);
                }}
                suffix={
                  get(formProps.errors, `contact.user.lastname`) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
                disabled={locked}
              />
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Input
                label={TRANSLATION.FORM.FIRST_NAME.LABEL}
                placeholder={TRANSLATION.FORM.FIRST_NAME.PLACEHOLDER}
                className={classNames({
                  valid: get(values, "contact.user.firstname"),
                })}
                {...bindInputProps({
                  name: `contact.user.firstname`,
                  values,
                  setFieldValue,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  setFieldValue(`contact.user.firstname`, value);
                }}
                suffix={
                  get(formProps.errors, `contact.user.firstname`) ? (
                    <img src={InputError} alt="" />
                  ) : (
                    <img src={InputChecked} alt="" />
                  )
                }
                disabled={locked}
              />
            </Col>
            {get(values, "contact.user.firstname") &&
              get(values, "contact.user.lastname") && (
                <>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Input
                      label={TRANSLATION.FORM.PHONE.LABEL}
                      placeholder={TRANSLATION.FORM.PHONE.PLACEHOLDER}
                      inputMode="numeric"
                      type="phone"
                      maxLength={10}
                      className={classNames({
                        valid: get(
                          values,
                          `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                        ),
                      })}
                      {...bindInputProps({
                        name: `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                      onChange={({ target: { value } }) => {
                        setFieldValue(
                          `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                          value,
                        );
                      }}
                      suffix={
                        get(
                          formProps.errors,
                          `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                        ) ? (
                          <img src={InputError} alt="" />
                        ) : (
                          <img src={InputChecked} alt="" />
                        )
                      }
                      disabled={locked}
                      right={
                        <HelpYellow
                          size={24}
                          onClick={() => setOpenModalPhone(true)}
                        />
                      }
                    />
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <Input
                      label={TRANSLATION.FORM.EMAIL.LABEL}
                      placeholder={TRANSLATION.FORM.EMAIL.PLACEHOLDER}
                      className={classNames({
                        valid: get(
                          values,
                          `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                        ),
                      })}
                      inputMode="numeric"
                      {...bindInputProps({
                        name: `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                      onChange={({ target: { value } }) => {
                        setFieldValue(
                          `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                          value,
                        );
                      }}
                      suffix={
                        get(
                          formProps.errors,
                          `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                        ) ? (
                          <img src={InputError} alt="" />
                        ) : (
                          <img src={InputChecked} alt="" />
                        )
                      }
                      disabled={locked}
                      right={
                        <HelpYellow
                          size={24}
                          onClick={() => setOpenModalEmail(true)}
                        />
                      }
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Input
                      type="checkbox"
                      label={TRANSLATION.FORM.OPT_INS.CONTACT}
                      checked={get(values, "contact.acceptContact")}
                      {...bindInputProps({
                        name: "contact.acceptContact",
                        values,
                        ...formProps,
                      })}
                      onChange={({ target: { checked } }) => {
                        setFieldValue("contact.acceptContact", checked);
                      }}
                      disabled={locked}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <Input
                      type="checkbox"
                      label={TRANSLATION.FORM.OPT_INS.MAIL_ME}
                      checked={get(values, "contact.acceptDigital")}
                      {...bindInputProps({
                        name: "contact.acceptDigital",
                        values,
                        ...formProps,
                      })}
                      onChange={({ target: { checked } }) => {
                        setFieldValue("contact.acceptDigital", checked);
                      }}
                      disabled={locked}
                    />
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24}>
                    <InfoBox
                      variant="danger"
                      text={TRANSLATION.FORM.INFO}
                      icon={<QuickActionRed size={48} />}
                    />
                  </Col>
                </>
              )}
          </Row>
        </div>

        <ActionButtons
          disabled={!isEmpty(formProps.errors)}
          onNext={handleSubmit}
          buttonText={TRANSLATION.NEXT}
          {...(isMobile && { onBack })}
          className={classNames({
            "w-100": isMobile,
          })}
        />

        <Footer variant="left" type="create" />
      </Layout>

      {/* <div className="info__area">
        <Policy size={24} />
        <span className="info__area--link" onClick={() => setOpenInfoModal(true)}>{TRANSLATION.POLICY}</span>
        </div>
      <Drawer
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        className="lmf__drawer"
        title={TRANSLATION.POLICY_CONTENT.TITLE}
        width={500}
      >
        <>
          <div className="lmf__drawer--content">
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_1}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_2}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_3}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_4}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_5}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_6}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_7}}></p>

            <ul>
              <li dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_OPTS.OPT_1}}></li>
              <li dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_OPTS.OPT_2}}></li>
              <li dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_OPTS.OPT_3}}></li>
            </ul>

            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_8}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_9}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_10}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_11}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_12}}></p>
            <p dangerouslySetInnerHTML={{  __html: TRANSLATION.POLICY_CONTENT.P_13}}></p>
          </div>
          <Button
            onClick={() => setOpenInfoModal(false)}
            className="w-full"
            type="white"
            size="middle"
          >
            <span>{TRANSLATION.POLICY_CONTENT.CLOSE}</span>
          </Button>
        </>
      </Drawer> */}
    </>
  );
};

export default View;
